import type { MessageBusConfig, MessageHandler, MessageSchema } from './message-bus.types'

/**
 * Strongly typed MessageBus class.
 * @template T - The map of messages and their respective payloads.
 */
export class MessageBus<T extends MessageSchema> {
  private readonly messageHandlers = new Map<keyof T, Set<MessageHandler<unknown>>>()
  private readonly handleMessageEvent: (event: MessageEvent<unknown>) => void

  constructor(private readonly config: MessageBusConfig) {
    this.handleMessageEvent = this.handleIncomingMessage.bind(this)
    window.addEventListener('message', this.handleMessageEvent)
  }

  /**
   * Subscribes to a message with a handler.
   */
  public subscribe<Message extends keyof T>(messageType: Message, handler: MessageHandler<T[Message]>): void {
    if (!this.messageHandlers.has(messageType)) {
      this.messageHandlers.set(messageType, new Set())
    }
    (this.messageHandlers.get(messageType) as Set<MessageHandler<T[Message]>>).add(handler)
  }

  /**
   * Unsubscribes a handler from a message.
   */
  public unsubscribe<Message extends keyof T>(messageType: Message, handler: MessageHandler<T[Message]>): void {
    const handlers = this.messageHandlers.get(messageType) as Set<MessageHandler<T[Message]>> | undefined
    if (!handlers)
      return

    handlers.delete(handler)
    if (handlers.size === 0) {
      this.messageHandlers.delete(messageType)
    }
  }

  /**
   * Registers a message handler that triggers only once.
   */
  public subscribeOnce<Message extends keyof T>(messageType: Message, handler: MessageHandler<T[Message]>): void {
    const wrapper: MessageHandler<T[Message]> = (messageData) => {
      this.unsubscribe(messageType, wrapper)
      handler(messageData)
    }
    this.subscribe(messageType, wrapper)
  }

  /**
   * Publishes a message to the target window.
   */
  public publish<Message extends keyof T>(messageType: Message, payload?: T[Message]): void {
    for (const origin of this.config.allowedOrigins) {
      this.config.target?.postMessage({ messageType, payload }, origin)
    }
  }

  /**
   * Cleans up all message listeners and handlers.
   */
  public destroy(): void {
    window.removeEventListener('message', this.handleMessageEvent)
    this.messageHandlers.clear()
  }

  /**
   * Handles incoming postMessage messages.
   */
  private handleIncomingMessage(event: MessageEvent<any>): void {
    if (typeof event.data !== 'object' || event.data === null || !event.data.messageType)
      return

    const { messageType, payload } = event.data as { messageType: keyof T, payload: T[keyof T] }
    const origin = event.origin

    if (!this.config.allowedOrigins.includes('*') && !this.config.allowedOrigins.includes(origin))
      return

    const handlers = this.messageHandlers.get(messageType) as Set<MessageHandler<T[keyof T]>> | undefined
    if (handlers) {
      handlers.forEach(handler => handler(payload))
    }
    else {
      console.warn(`[MessageBus] Unhandled message: ${String(messageType)} from ${origin}`)
    }
  }
}
